import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Box, Clapperboard, Home, Images, MessageSquareText, Plus, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer } from '../../../common/components/PageContainer';
import { EmptyState, PillTabs } from '../../../components/design-system/admin';
import {
    ContentPack,
    ContentPackFilter,
    ContentPackList,
    ContentPackSettingsView,
    useContentPacksApi,
} from '../../../common/hooks/use-content-packs-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { ItemAction, PackCard } from '../../../modules/vendor/content-packs/pack-card';
import { MakePackDialog } from '../../../modules/vendor/content-packs/make-pack-dialog';
import { PackSettingsCard } from '../../../modules/vendor/content-packs/pack-settings-card';
import { isInFlight } from '../../../modules/vendor/content-packs/content-pack-view';

/** How often the page asks again while something is being made. */
const POLL_MS = 6000;

/**
 * Content packs — Marketing. For each product: a room shot with the real
 * product correctly laid, a packshot, a clip and posts, made by Ardealy and
 * waiting for the vendor's yes. Approved pictures go to the product's page in
 * the shop; approved posts become scheduled posts. Nothing goes out unasked.
 *
 * Links: `?pack=<uuid>` (from the "pack ready" notification) highlights a
 * pack; `?make=<productUuid>` (from the catalogue) opens "Make a pack" with
 * that product chosen.
 */
export const ContentPacksPage = () => {
    const { t } = useTranslation();
    const api = useContentPacksApi();
    const [params, setParams] = useSearchParams();
    const [filter, setFilter] = useState<ContentPackFilter>('waiting');
    const [list, setList] = useState<ContentPackList | null>(null);
    const [settings, setSettings] = useState<ContentPackSettingsView | null>(null);
    const [error, setError] = useState(false);
    const [dialog, setDialog] = useState(false);
    const [preselect, setPreselect] = useState<string[] | undefined>();
    const highlight = params.get('pack') || undefined;
    const firstLoad = useRef(true);

    const load = useCallback(
        async (which: ContentPackFilter) => {
            try {
                const res = await api.list({ status: which, limit: 30 });
                setList(res);
                setError(false);
                // A pack with nothing waiting: show all instead of an empty "waiting" tab on first visit.
                if (firstLoad.current) {
                    firstLoad.current = false;
                    if (which === 'waiting' && !res.items.length && res.counts.all > 0) setFilter('all');
                }
            } catch {
                setError(true);
            }
        },
        [api],
    );

    useEffect(() => {
        void load(filter);
    }, [filter, load]);

    useEffect(() => {
        api.settings().then(setSettings).catch(() => setSettings(null));
    }, [api]);

    // Keep asking while pictures are being made or posts written.
    useEffect(() => {
        if (!list || !isInFlight(list.items)) return undefined;
        const timer = setTimeout(() => void load(filter), POLL_MS);
        return () => clearTimeout(timer);
    }, [list, filter, load]);

    // Deep links.
    useEffect(() => {
        const make = params.get('make');
        if (make) {
            setPreselect([make]);
            setDialog(true);
            params.delete('make');
            setParams(params, { replace: true });
        }
    }, [params, setParams]);
    useEffect(() => {
        if (!highlight || !list) return;
        document.getElementById(`pack-${highlight}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [highlight, list]);

    const replace = (pack: ContentPack) =>
        setList((l) => (l ? { ...l, items: l.items.map((p) => (p.uuid === pack.uuid ? pack : p)) } : l));

    const onItemAction = async (pack: ContentPack, key: string, action: ItemAction) => {
        try {
            const next =
                action === 'approve'
                    ? await api.approve(pack.uuid, key)
                    : action === 'reject'
                      ? await api.reject(pack.uuid, key)
                      : action === 'push'
                        ? await api.retryPush(pack.uuid, key)
                        : await api.regenerate(pack.uuid, key);
            replace(next);
            void load(filter);
        } catch (e) {
            const body = await readApiError(e);
            toast.error(body?.message ? String(body.message) : t('CONTENT_PACKS.ERRORS.ACTION'));
        }
    };

    const onApproveAll = async (pack: ContentPack) => {
        try {
            replace(await api.approveAll(pack.uuid));
            void load(filter);
        } catch {
            toast.error(t('CONTENT_PACKS.ERRORS.ACTION'));
        }
    };

    const openDialog = () => {
        setPreselect(undefined);
        setDialog(true);
    };

    const counts = list?.counts;
    const nothingYet = !!list && counts?.all === 0;

    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.MARKETING.TITLE') }, { label: t('R2R.NAV.CONTENT_PACKS') }]}>
            <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-6" data-testid="content-packs-page">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="max-w-[640px]">
                        <h1 className="font-heading text-[22px] font-medium tracking-tight">{t('CONTENT_PACKS.TITLE')}</h1>
                        <p className="mt-1 text-sm text-muted-foreground">{t('CONTENT_PACKS.SUBTITLE')}</p>
                    </div>
                    <Button onClick={openDialog} data-testid="make-pack">
                        <Plus data-icon="inline-start" />
                        {t('CONTENT_PACKS.MAKE')}
                    </Button>
                </div>

                <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
                    <div className="flex min-w-0 flex-col gap-4">
                        {!nothingYet && (
                            <PillTabs
                                ariaLabel={t('CONTENT_PACKS.FILTER.LABEL')}
                                value={filter}
                                onChange={setFilter}
                                options={[
                                    { value: 'waiting', label: t('CONTENT_PACKS.FILTER.WAITING'), count: counts?.waiting },
                                    { value: 'approved', label: t('CONTENT_PACKS.FILTER.APPROVED'), count: counts?.approved },
                                    { value: 'all', label: t('CONTENT_PACKS.FILTER.ALL'), count: counts?.all },
                                ]}
                            />
                        )}

                        {error && <p className="text-sm text-destructive">{t('CONTENT_PACKS.ERRORS.LOAD')}</p>}

                        {!list && !error && (
                            <div className="flex flex-col gap-4">
                                <Skeleton className="h-[360px] rounded-xl" />
                                <Skeleton className="h-[360px] rounded-xl" />
                            </div>
                        )}

                        {nothingYet && <WhatIsAPack onMake={openDialog} />}

                        {list && !nothingYet && !list.items.length && (
                            <div className="rounded-xl border bg-card">
                                <EmptyState
                                    icon={<Images />}
                                    title={t(filter === 'approved' ? 'CONTENT_PACKS.EMPTY.NONE_APPROVED' : 'CONTENT_PACKS.EMPTY.NONE_WAITING')}
                                />
                            </div>
                        )}

                        {list?.items.map((pack) => (
                            <PackCard key={pack.uuid} pack={pack} highlighted={pack.uuid === highlight} onItemAction={onItemAction} onApproveAll={onApproveAll} />
                        ))}
                    </div>

                    <aside className="flex flex-col gap-4 lg:sticky lg:top-4">
                        {settings ? (
                            <PackSettingsCard
                                view={settings}
                                onSave={async (patch) => {
                                    setSettings(await api.updateSettings(patch));
                                }}
                            />
                        ) : (
                            <Skeleton className="h-[480px] rounded-xl" />
                        )}
                    </aside>
                </div>
            </div>

            <MakePackDialog
                open={dialog}
                onOpenChange={setDialog}
                defaultKinds={settings?.settings.kinds ?? ['room', 'packshot', 'clip', 'post']}
                defaultPosts={settings?.settings.postsPerPack ?? 3}
                preselect={preselect}
                onMade={() => {
                    setFilter('waiting');
                    void load('waiting');
                }}
            />
        </PageContainer>
    );
};

/** The first visit: what a pack is, and that nothing goes out without a yes. */
const WhatIsAPack: React.FC<{ onMake: () => void }> = ({ onMake }) => {
    const { t } = useTranslation();
    const rows: Array<[React.ReactNode, string]> = [
        [<Home key="r" />, 'CONTENT_PACKS.EMPTY.ROOM'],
        [<Box key="p" />, 'CONTENT_PACKS.EMPTY.PACKSHOT'],
        [<Clapperboard key="c" />, 'CONTENT_PACKS.EMPTY.CLIP'],
        [<MessageSquareText key="m" />, 'CONTENT_PACKS.EMPTY.POSTS'],
    ];
    return (
        <div className="rounded-xl border bg-card p-6 sm:p-8" data-testid="content-packs-empty">
            <span className="flex size-10 items-center justify-center rounded-lg border bg-muted/50 text-muted-foreground">
                <Images className="size-5" />
            </span>
            <h2 className="mt-4 text-base font-medium">{t('CONTENT_PACKS.EMPTY.TITLE')}</h2>
            <p className="mt-1 max-w-[560px] text-sm text-muted-foreground">{t('CONTENT_PACKS.EMPTY.BODY')}</p>
            <ul className="mt-4 flex flex-col gap-2.5">
                {rows.map(([icon, key]) => (
                    <li key={key} className="flex items-start gap-3 text-sm">
                        <span className="mt-0.5 flex text-muted-foreground [&_svg]:size-4">{icon}</span>
                        {t(key)}
                    </li>
                ))}
            </ul>
            <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="size-3.5" />
                {t('CONTENT_PACKS.EMPTY.SAFE')}
            </p>
            <Button className="mt-5" onClick={onMake}>
                <Plus data-icon="inline-start" />
                {t('CONTENT_PACKS.EMPTY.ACTION')}
            </Button>
        </div>
    );
};

export default ContentPacksPage;
